import * as Cesium from 'cesium'

export interface Dms { degree: number; minute: number; second: number; }

export const decimalToDms = (decimal: number): Dms => {
  const degree = Math.floor(decimal)
  const minuteDecimal = (decimal - degree) * 60
  const minute = Math.floor(minuteDecimal)
  const second = (minuteDecimal - minute) * 60
  return { degree, minute, second: parseFloat(second.toFixed(4)) }
}

export const dmsToDecimal = (dms: Dms): number => {
  return dms.degree + dms.minute / 60 + dms.second / 3600
}

// Cesium坐标类型定义
export interface CesiumCartesian3 { x: number; y: number; z: number; }
export interface CesiumCartographic { longitude: number; latitude: number; height: number; }

// ======================== Cesium辅助函数 ========================

/** * 计算两点之间的Cesium椭球体表面距离（测地线距离） */
export const calculateGeodesicDistance = (
  point1: Cesium.Cartesian3,
  point2: Cesium.Cartesian3
): number => {
  return Cesium.Cartesian3.distance(point1, point2)
}

/** * 计算多边形在Cesium椭球体上的面积（测地面积） */
export const calculateCesiumPolygonArea = (positions: CesiumCartographic[]): number => {
  if (positions.length < 3) return 0
  let area = 0
  const cartographics = positions.map(pos => pos)
  const length = cartographics.length

  for (let i = 0; i < length; i++) {
    const j = (i + 1) % length
    const k = (i + 2) % length

    const lon1 = Cesium.Math.toDegrees(cartographics[i].longitude)
    const lat1 = Cesium.Math.toDegrees(cartographics[i].latitude)
    const lon2 = Cesium.Math.toDegrees(cartographics[j].longitude)
    const lat2 = Cesium.Math.toDegrees(cartographics[j].latitude)
    const lon3 = Cesium.Math.toDegrees(cartographics[k].longitude)
    const lat3 = Cesium.Math.toDegrees(cartographics[k].latitude)

    const a = calcGreatCircleDistance(lat1, lon1, lat2, lon2)
    const b = calcGreatCircleDistance(lat2, lon2, lat3, lon3)
    const c = calcGreatCircleDistance(lat3, lon3, lat1, lon1)

    const s = (a + b + c) / 2
    const triangleArea = Math.sqrt(s * (s - a) * (s - b) * (s - c))

    area += triangleArea
  }

  return area
}
export const calcGreatCircleDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lon2 - lon1) * Math.PI / 180

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}
// ======== 多边形周长 ========
export const calculateCesiumPolygonPerimeter = (positions: CesiumCartographic[]): number => {
  if (positions.length < 2) return 0
  let perimeter = 0
  const n = positions.length
  for (let i = 0; i < n; i++) {
    const point = Cesium.Cartesian3.fromDegrees(positions[i].longitude, positions[i].latitude, positions[i].height || 0)
    const point2 = Cesium.Cartesian3.fromDegrees(positions[(i + 1) % n].longitude, positions[(i + 1) % n].latitude, positions[(i + 1) % n].height || 0)
    perimeter += calculateGeodesicDistance(point, point2)
  }
  return perimeter
}
/** * 计算多边形的高度差（基于Cartographic坐标） */
export const calculateCesiumPolygonHeightDifferenceCartographic = (positions: CesiumCartographic[]): number => {
  if (positions.length < 2) return 0
  let minHeight = positions[0].height
  let maxHeight = positions[0].height
  for (let i = 1; i < positions.length; i++) {
    const height = positions[i].height
    if (height < minHeight) minHeight = height
    if (height > maxHeight) maxHeight = height
  }
  return maxHeight - minHeight
}

// ======================== Cesium圆形计算 ========================

export interface CesiumCircle { center: CesiumCartographic; radius: number; }

/** * 计算Cesium圆形的面积（基于椭球体的测地面积） */
export const calculateCesiumCircleArea = (circle: CesiumCircle): number => {
  // 近似计算圆形面积（πr²）
  return Math.PI * circle.radius * circle.radius
}

/** * 计算Cesium圆形的周长（测地周长） */
export const calculateCesiumCircleCircumference = (circle: CesiumCircle): number => {
  // 近似计算圆形周长（2πr）
  return 2 * Math.PI * circle.radius
}

/** * 生成Cesium圆形的采样点 */
export const generateCesiumCircleSamples = (circle: CesiumCircle, sampleCount: number = 36): CesiumCartographic[] => {
  const samples: CesiumCartographic[] = []
  const { center, radius } = circle

  for (let i = 0; i < sampleCount; i++) {
    const angle = (2 * Math.PI * i) / sampleCount
    // 在球面上计算点（近似）
    const lon = center.longitude + (radius / 6378137) * Math.cos(angle)
    const lat = center.latitude + (radius / 6378137) * Math.sin(angle)
    samples.push({ longitude: lon, latitude: lat, height: center.height })
  }
  return samples
}

// ======================== 与Cesium API集成的综合计算函数 ========================

/** * 计算Cesium多边形的综合信息 */
export const calculateCesiumPolygonInfo = (positions: CesiumCartographic[]): {
  area: number;
  perimeter: number;
  heightDifference: number;
  centroid?: CesiumCartographic;
} => {
  const area = calculateCesiumPolygonArea(positions)
  const perimeter = calculateCesiumPolygonPerimeter(positions)
  const heightDifference = calculateCesiumPolygonHeightDifferenceCartographic(positions)

  // 计算质心
  let sumLon = 0
  let sumLat = 0
  let sumHeight = 0
  for (const position of positions) {
    sumLon += position.longitude
    sumLat += position.latitude
    sumHeight += position.height
  }
  const centroid: CesiumCartographic = {
    longitude: sumLon / positions.length,
    latitude: sumLat / positions.length,
    height: sumHeight / positions.length
  }

  return { area, perimeter, heightDifference, centroid }
}

/** * 计算Cesium圆形的综合信息 */
export const calculateCesiumCircleInfo = (circle: CesiumCircle, sampleCount?: number): {
  area: number;
  circumference: number;
  height: number;
  samples?: CesiumCartographic[];
} => {
  const area = calculateCesiumCircleArea(circle)
  const circumference = calculateCesiumCircleCircumference(circle)
  let samples: CesiumCartographic[] | undefined

  if (sampleCount && sampleCount > 0) {
    samples = generateCesiumCircleSamples(circle, sampleCount)
  }

  return { area, circumference, height: circle.center.height, samples }
}

// ======================== 坐标转换辅助函数 ========================

/** * 将十进制度坐标转换为Cesium弧度坐标 */
export const degreesToRadians = (degrees: number): number => {
  return degrees * Math.PI / 180
}

/** * 将经纬度（十进制度）转换为Cesium Cartographic坐标 */
export const latLonToCartographic = (longitude: number, latitude: number, height: number = 0): CesiumCartographic => {
  return {
    longitude: longitude,
    latitude: latitude,
    height
  }
}

/** * 将经纬度数组转换为Cesium Cartographic数组 */
export const latLonArrayToCartographicArray = (coordinates: [number, number, number?][]): CesiumCartographic[] => {
  return coordinates.map(coord => ({
    longitude: degreesToRadians(coord[0]),
    latitude: degreesToRadians(coord[1]),
    height: coord[2] || 0
  }))
}

/** * 创建Cesium圆形 */
export const createCesiumCircle = (centerLon: number, centerLat: number, radius: number, height: number = 0): CesiumCircle => {
  return {
    center: latLonToCartographic(centerLon, centerLat, height),
    radius
  }
}
